import { useState } from "react";
import { Link } from "react-router-dom";
import { trpc } from "../lib/trpc";
import { ImportModal } from "../components/import/ImportModal";
import { Icon } from "../components/ui/Icon";

/**
 * Yönetici Dashboard — GENTAŞ referans yerleşimi.
 * .page + .page-head + .dash-kpi-grid (.dash-kpi sol-accent) + .split-2 (.d-card) + .bar-chart.
 * Referans dışı custom kart kullanılmaz; tüm bloklar blocks.css'ten.
 */
function fmtNum(n: number): string { return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(n); }
function fmtMoney(n: number): string { return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n); }
function fmtQty(n: number): string { return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n); }

export function DashboardPage() {
  const [importOpen, setImportOpen] = useState(false);
  const dashQ = trpc.reports.dashboard.useQuery(undefined, { staleTime: 15_000 });
  const d = dashQ.data;

  const maxProd = Math.max(1, ...(d?.recentProduction ?? []).map((p) => p.qty));

  return (
    <main className="page">
      <header className="page-head">
        <div>
          <h1 className="page-title">Yönetici Dashboard</h1>
          <p className="page-sub">STEPERP · gerçek zamanlı operasyon özeti</p>
        </div>
        <button type="button" className="btn-md primary" onClick={() => setImportOpen(true)}>
          <Icon name="inbox" size={15} />Excel ile İçeri Aktar
        </button>
      </header>

      {dashQ.isError ? (
        <div className="form-banner-err">
          <Icon name="alertTriangle" size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            Dashboard verileri yüklenemedi.{dashQ.error?.message ? ` (${dashQ.error.message})` : ""}{" "}
            <button type="button" className="btn-md ghost" style={{ height: 24, padding: "0 8px", fontSize: 12 }} onClick={() => dashQ.refetch()}>Tekrar dene</button>
          </span>
        </div>
      ) : dashQ.isLoading || !d ? (
        <>
          <div className="dash-kpi-grid" style={{ marginBottom: 14 }}>
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skel" style={{ height: 74 }} />)}
          </div>
          <div className="split-2">
            <div className="skel" style={{ height: 248 }} />
            <div className="skel" style={{ height: 248 }} />
          </div>
        </>
      ) : (
        <>
          <div className="dash-kpi-grid" style={{ marginBottom: 14 }}>
            {/* Stok değeri: Genel Bakış KPI'ı ve envanter raporuyla AYNI tanım (api stock-value.ts tesisStokDegeri).
                Değeri bilinmeyen lot varsa toplam alt sınırdır → "≥" + uyarı satırı (fiyatsız miktar 0 sayılmaz). */}
            <DashKpi label="Stok Değeri"
              value={`${d.stockValuationUnknownLots > 0 ? "≥ " : ""}${fmtMoney(d.stockValuation)}`} unit="₺" color="var(--primary)"
              sub={d.stockValuationUnknownLots > 0 ? `${fmtNum(d.stockValuationUnknownLots)} lotun değeri bilinmiyor` : "değeri bilinen lotlar"}
              subTone={d.stockValuationUnknownLots > 0 ? "wrn" : undefined} to="/inventory" />
            {/* "Açık Sipariş" = genel MRP talep kümesi (Onaylı · Üretimde · Hazır, api mrp.ts MRP_TALEP_DURUMLARI). Siparişler
                ekranındaki "Açık siparişler" kısayolu farklı bir küme (Taslak · Onaylı · Üretimde) — alt metin kümeyi söyler. */}
            <DashKpi label="Açık Sipariş"  value={fmtNum(d.activeOrders)}     unit="adet"   color="var(--info)"    sub="onaylı · üretimde · hazır" to="/orders" />
            <DashKpi label="Açık İş Emri"  value={fmtNum(d.openWorkOrders)}   unit="WO"     color="var(--warning)" sub="üretimde"         to="/production" />
            <DashKpi label="QC Bekleyen"   value={fmtNum(d.pendingQc)}        unit="lot"    color="var(--danger)"  sub="kontrol kuyruğu"  to="/qc" />
            <DashKpi label="Stoktaki Lot"  value={fmtNum(d.lotsInStock)}      unit="lot"    color="var(--success)" sub="depoda"           to="/inventory" />
            <DashKpi label="Aktif Reçete"  value={fmtNum(d.recipeCount)}      unit="reçete" color="var(--accent)"  sub="tanımlı"          to="/recipes" />
          </div>

          <div className="split-2">
            {/* Üretim grafiği */}
            <section className="d-card">
              <div className="d-card-head">
                <div className="left">
                  <span className="t">Üretim Çıktısı</span>
                  <span className="s">son 14 gün</span>
                </div>
              </div>
              <div className="d-card-body">
                {d.recentProduction.length === 0 ? (
                  <div className="d-empty">
                    <Icon name="chart" size={26} />
                    <span className="m">Bu dönemde üretim hareketi yok.</span>
                  </div>
                ) : (
                  <>
                    <div className="bar-chart" role="img" aria-label={`Son 14 günde üretim çıktısı: ${d.recentProduction.map((p) => `${p.date} ${fmtQty(p.qty)} birim`).join("; ")}`}>
                      {d.recentProduction.map((p) => (
                        <div key={p.date} className="bar" data-val={`${fmtQty(p.qty)} · ${p.count} hareket`} style={{ height: `${Math.max(3, (p.qty / maxProd) * 100)}%` }} />
                      ))}
                    </div>
                    <div className="chart-x">
                      {d.recentProduction.map((p) => <span key={p.date}>{p.date.slice(5)}</span>)}
                    </div>
                  </>
                )}
              </div>
            </section>

            {/* Düşük stok uyarısı */}
            <section className="d-card">
              <div className="d-card-head">
                <div className="left"><span className="t">Düşük Stok Uyarısı</span></div>
                <Link to="/mrp" className="dcard-link">MRP <Icon name="arrowRight" size={12} /></Link>
              </div>
              <div className="d-card-body flush">
                {d.lowStockItems.length === 0 ? (
                  <div className="d-empty">
                    <Icon name="checkCircle" size={26} />
                    <span className="m">Reorder noktası altında hammadde yok.</span>
                  </div>
                ) : (
                  <div className="list-card">
                    {d.lowStockItems.map((l) => (
                      <div key={l.code} className="list-item">
                        <Icon name="alertTriangle" size={14} style={{ color: "var(--warning)", flexShrink: 0 }} />
                        <div className="body">
                          <span className="t">{l.name} <span className="mono" style={{ fontSize: 10.5, color: "var(--neutral-400)" }}>{l.code}</span></span>
                        </div>
                        <span className="amount" style={{ color: "var(--warning)", fontWeight: 600 }}>{fmtQty(l.onHand)} / {fmtQty(l.reorderPoint)} {l.uom}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* QC bekleyen lotlar */}
          {d.pendingQcLots.length > 0 ? (
            <section className="d-card" style={{ marginTop: 16 }}>
              <div className="d-card-head">
                <div className="left"><span className="t">QC Bekleyen Lotlar</span></div>
                <Link to="/qc" className="dcard-link">Kalite Kontrol <Icon name="arrowRight" size={12} /></Link>
              </div>
              <div className="d-card-body flush">
                <div className="list-card">
                  {d.pendingQcLots.map((q) => (
                    <div key={q.code} className="list-item">
                      <Icon name="checkCircle" size={14} style={{ color: "var(--warning)", flexShrink: 0 }} />
                      <div className="body">
                        <span className="t"><span className="mono" style={{ color: "var(--warning)", fontWeight: 600 }}>{q.code}</span> {q.itemName}</span>
                      </div>
                      <span className="amount">{fmtQty(q.qty)} {q.uom}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          ) : null}
        </>
      )}

      <ImportModal open={importOpen} onClose={() => setImportOpen(false)} />
    </main>
  );
}

function DashKpi({ label, value, unit, color, sub, subTone, to }: {
  label: string; value: string; unit: string; color: string; sub: string; subTone?: "wrn"; to: string;
}) {
  return (
    <Link to={to} className="dash-kpi" style={{ ["--kpi-color" as string]: color }}>
      <div className="head"><span className="lbl">{label}</span></div>
      <div className="val">{value}<span className="unit">{unit}</span></div>
      {/* Uyarı tonu = renk + ikon + metin (renk tek başına yetmez). */}
      <div className={`sub${subTone ? ` ${subTone}` : ""}`}>{subTone === "wrn" ? <Icon name="alertTriangle" size={11} /> : null}{sub}</div>
    </Link>
  );
}
